import { useEffect, useRef } from 'react';
import { hashKey } from '@tanstack/react-query';

import { useUrlSearchState } from './useUrlSearchState';
import { useScrollPositionStore } from './useScrollPositionStore';
import { useListings } from './queries/useListings';

export const useAdjustScrollPosition = () => {
  const { queryKey } = useListings();
  const listingsQueryKey = hashKey(queryKey);
  const { scrollPosition, setScrollPosition } = useScrollPositionStore();
  const { isSetStateFromUrlDone } = useUrlSearchState();
  const previousKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (scrollPosition) {
      window.scrollTo({ top: scrollPosition, left: 0, behavior: 'auto' });
      setScrollPosition(0);
      if (isSetStateFromUrlDone) previousKeyRef.current = listingsQueryKey;
      return;
    }

    // URL hydration applies the first query. Scrolling there would jump the page on load.
    if (!isSetStateFromUrlDone) return;

    if (previousKeyRef.current === undefined) {
      previousKeyRef.current = listingsQueryKey;
      return;
    }

    if (previousKeyRef.current === listingsQueryKey) return;

    const frame = requestAnimationFrame(() => {
      previousKeyRef.current = listingsQueryKey;
      // Apply and reset can resolve from cache, so the scroll follows the committed query rather than isFetching.
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    });

    return () => cancelAnimationFrame(frame);
  }, [isSetStateFromUrlDone, listingsQueryKey, scrollPosition, setScrollPosition]);
};
